import Decimal from 'decimal.js';
import type { Month } from '@contracts/common';
import type { Money } from '@contracts/finance';

/**
 * Everything the finance screens need in order to *show* an amount, and nothing that could change one.
 *
 * An amount is a string from the moment the server sends it to the moment it is painted — grouping digits is
 * text work, not arithmetic, so a figure larger than `Number.MAX_SAFE_INTEGER` survives being read.
 */

/** `2026-01` as the company reads it. */
export const monthLabel = (month: Month): string => `${month.slice(5)}/${month.slice(0, 4)}`;

/**
 * One amount in đồng, exactly as the API's own validation spells it: a signed integer, no fraction, no
 * exponent, no separator, no currency mark, at most the 38 digits the column holds. Refusing it here is not
 * a second opinion — it is what stops a typed `120,5` from being rounded into a different amount downstream.
 */
export const AMOUNT = /^-?(0|[1-9][0-9]{0,37})$/;

/** The one minus sign every finance figure is written with (U+2212), so a page never mixes two. */
export const MINUS = '\u2212';

/** Thousands separated with a dot, a U+2212 minus in front, every digit preserved because none is parsed. */
export function formatMoney(value: string): string {
  const negative = value.startsWith('-');
  const digits = negative ? value.slice(1) : value;
  return (negative ? MINUS : '') + digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Enough significant digits for the 38 the column holds, so dividing never rounds a digit away. */
const Exact = Decimal.clone({ precision: 60, rounding: Decimal.ROUND_HALF_UP });
const TEN_MILLION = new Exact('10000000');
const BILLION = new Exact('1000000000');

/**
 * The short amount the project list and the contract rows show: from ten million đồng up, billions with two
 * decimals after a comma ("3,20 tỷ"); below that the whole amount in đồng ("999.999 ₫"). Division is decimal,
 * never a JS number, so a 30-digit value keeps every digit.
 */
export function formatMoneyShort(value: string): string {
  const amount = new Exact(value);
  if (amount.abs().lessThan(TEN_MILLION)) return `${formatMoney(value)} ₫`;
  const [whole = '0', cents = '00'] = amount.dividedBy(BILLION).toFixed(2).split('.');
  return `${formatMoney(whole)},${cents} tỷ`;
}

const MILLION = new Exact('1000000');
const THOUSAND = new Exact('1000');

type CompactUnit = 'tỷ' | 'tr' | '₫';

/** The unit an amount reads in: tỷ from a billion (or from 1.000 tr once rounded), tr from a million, else đồng. */
function unitOf(abs: Decimal): CompactUnit {
  if (abs.greaterThanOrEqualTo(BILLION)) return 'tỷ';
  if (abs.greaterThanOrEqualTo(MILLION)) {
    return abs.dividedBy(MILLION).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).greaterThanOrEqualTo(THOUSAND) ? 'tỷ' : 'tr';
  }
  return '₫';
}

/** The digits of a non-negative amount in `unit`, without the unit: "3,43", "940", "500.000". */
function digitsIn(abs: Decimal, unit: CompactUnit, digits: number): string {
  if (unit === '₫') return formatMoney(abs.toFixed(0));
  if (unit === 'tr') return formatMoney(abs.dividedBy(MILLION).toFixed(0, Decimal.ROUND_HALF_UP));
  const [whole = '0', fraction = ''] = abs.dividedBy(BILLION).toFixed(digits, Decimal.ROUND_HALF_UP).split('.');
  return fraction === '' ? formatMoney(whole) : `${formatMoney(whole)},${fraction}`;
}

/** A minus only for a figure that does not read as zero once rounded, so nothing ever shows "−0". */
const signed = (negative: boolean, text: string): string => (negative && /[1-9]/.test(text) ? MINUS + text : text);

/**
 * The short amount of the finance screens: "3,43 tỷ" from a billion up (`digits` decimals); "940 tr" from a million up
 * (whole millions, half up; 1.000 tr and more reads as tỷ); below a million the whole amount "500.000 ₫". `unit` 'tỷ'
 * forces tỷ ("0,20 tỷ"). Division is decimal, so a 30-digit amount keeps every digit.
 */
export function formatMoneyCompact(value: Money, digits = 2, unit?: 'tỷ'): string {
  const amount = new Exact(value);
  const abs = amount.abs();
  const u = unit ?? unitOf(abs);
  const text = digitsIn(abs, u, digits);
  return `${signed(amount.isNegative(), text)} ${u}`;
}

/**
 * The amount on a Map card's finance chip (S5 §10.2, mockup §3 "Đặt cọc 30tr · khoản chi"), as short as it reads:
 * "1,5 tỷ" from a billion up (one decimal, a ",0" dropped); "30tr" from a million up (whole millions, half up);
 * "500k" from a thousand up; below that "999đ". A figure that rounds up into the next unit reads in it (999,5 tr →
 * "1 tỷ", 999.999 → "1tr"). Decimal division, so a 38-digit amount keeps every digit.
 */
export function formatMoneyChip(value: Money): string {
  const amount = new Exact(value);
  const abs = amount.abs();
  const whole = (unit: Decimal) => abs.dividedBy(unit).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  let text: string;
  if (abs.greaterThanOrEqualTo(BILLION) || (abs.greaterThanOrEqualTo(MILLION) && whole(MILLION).greaterThanOrEqualTo(THOUSAND))) {
    const [int = '0', tenth = '0'] = abs.dividedBy(BILLION).toFixed(1, Decimal.ROUND_HALF_UP).split('.');
    text = `${formatMoney(int)}${tenth === '0' ? '' : `,${tenth}`} tỷ`;
  } else if (abs.greaterThanOrEqualTo(MILLION) || (abs.greaterThanOrEqualTo(THOUSAND) && whole(THOUSAND).greaterThanOrEqualTo(THOUSAND))) {
    text = `${formatMoney(whole(MILLION).toFixed(0))}tr`;
  } else if (abs.greaterThanOrEqualTo(THOUSAND)) {
    text = `${whole(THOUSAND).toFixed(0)}k`;
  } else {
    text = `${abs.toFixed(0)}đ`;
  }
  return signed(amount.isNegative(), text);
}

/** `formatMoneyCompact` with '+' in front of a positive amount ("+90 tr", "−80 tr"); zero stays "0 ₫". */
export function formatMoneyDelta(value: Money): string {
  const text = formatMoneyCompact(value);
  return new Exact(value).greaterThan(0) ? `+${text}` : text;
}

/**
 * Two amounts in the unit of the larger: "1,80 / 3,20 tỷ", "95 / 120 tr", "500.000 / 800.000 ₫"; a missing side is
 * '—'. `unit` 'tỷ' keeps both in tỷ, so a table column reads in one unit down its rows.
 */
export function formatMoneyPair(a: Money | null, b: Money | null, unit?: 'tỷ'): string {
  const values = [a, b].map((v) => (v === null ? null : new Exact(v)));
  const largest = values.reduce<Decimal>((max, v) => (v !== null && v.abs().greaterThan(max) ? v.abs() : max), new Exact(0));
  const u = unit ?? unitOf(largest);
  const side = (v: Decimal | null) => (v === null ? '—' : signed(v.isNegative(), digitsIn(v.abs(), u, 2)));
  return `${side(values[0] ?? null)} / ${side(values[1] ?? null)} ${u}`;
}

/** Whole millions for the months table: "3.470", "−130"; `signed` writes "+50" for a positive figure; null → '—'. */
export function formatMillions(value: Money | null, signedPlus = false): string {
  if (value === null) return '—';
  const amount = new Exact(value);
  const text = signed(amount.isNegative(), formatMoney(amount.abs().dividedBy(MILLION).toFixed(0, Decimal.ROUND_HALF_UP)));
  return signedPlus && !text.startsWith(MINUS) && /[1-9]/.test(text) ? `+${text}` : text;
}

/** A 2-decimal percentage as a whole one: '28.13' → '28%', '-5.50' → '−6%' (half away from zero); null → '—'. */
export function formatPercent(value: string | null): string {
  if (value === null) return '—';
  const amount = new Exact(value);
  return `${signed(amount.isNegative(), amount.abs().toFixed(0, Decimal.ROUND_HALF_UP))}%`;
}

/** A percentage to one decimal with a decimal comma, a trailing ',0' dropped: '4.50' → '4,5%', '4.00' → '4%'. */
export function formatPercentTenths(value: string | null): string {
  if (value === null) return '—';
  const amount = new Exact(value);
  const text = amount.abs().toFixed(1, Decimal.ROUND_HALF_UP).replace(/\.0$/, '').replace('.', ',');
  return `${signed(amount.isNegative(), text)}%`;
}

/** a − b, exact, as an integer string; never '-0'. */
export function moneyMinus(a: Money, b: Money): Money {
  const text = new Exact(a).minus(b).toFixed(0);
  return text === '-0' ? '0' : text;
}
